import QRCodeStyling, {
  Options as QRStylingOptions,
  DotType as LibDotType,
  CornerSquareType as LibCornerSquareType,
  CornerDotType as LibCornerDotType,
  ErrorCorrectionLevel as LibErrorCorrectionLevel,
  GradientType as LibGradientType
} from 'qr-code-styling';
import { QRDesign } from '@/types/qr';

/**
 * Maps QR Studio's internal QRDesign configuration to qr-code-styling options.
 */
export function createQRStylingOptions(
  data: string,
  design: QRDesign,
  size: number = 320
): QRStylingOptions {
  // Dot styling
  const dotsOptions: QRStylingOptions['dotsOptions'] = {
    type: (design.dotsType || 'square') as LibDotType,
    color: design.dotsColor || '#1d1d1f'
  };

  if (design.dotsGradient && design.dotsGradient.colorStops.length >= 2) {
    dotsOptions.gradient = {
      type: design.dotsGradient.type as LibGradientType,
      rotation: (design.dotsGradient.rotation ?? 45) * (Math.PI / 180),
      colorStops: design.dotsGradient.colorStops
    };
  }

  // Corner squares (Outer finder)
  const cornersSquareOptions: QRStylingOptions['cornersSquareOptions'] = {
    type: (design.cornersSquareType || 'square') as LibCornerSquareType,
    color: design.cornersSquareColor || design.dotsColor || '#1d1d1f'
  };

  // Corner dots (Inner finder)
  const cornersDotOptions: QRStylingOptions['cornersDotOptions'] = {
    type: (design.cornersDotType || 'square') as LibCornerDotType,
    color: design.cornersDotColor || design.dotsColor || '#1d1d1f'
  };

  // Background
  const backgroundOptions: QRStylingOptions['backgroundOptions'] = {
    color: design.isTransparent ? 'rgba(255,255,255,0)' : (design.backgroundColor || '#ffffff')
  };

  if (!design.isTransparent && design.backgroundGradient && design.backgroundGradient.colorStops.length >= 2) {
    backgroundOptions.gradient = {
      type: design.backgroundGradient.type as LibGradientType,
      rotation: (design.backgroundGradient.rotation ?? 45) * (Math.PI / 180),
      colorStops: design.backgroundGradient.colorStops
    };
  }

  // Image / Logo
  const imageOptions: QRStylingOptions['imageOptions'] = {
    hideBackgroundDots: design.logo?.hideBackgroundDots ?? true,
    imageSize: design.logo?.size ?? 0.25,
    margin: design.logo?.margin ?? 4,
    crossOrigin: 'anonymous'
  };

  return {
    width: size,
    height: size,
    type: 'canvas',
    data: data || 'https://qr.planmaster.in',
    margin: design.margin ?? 12,
    qrOptions: {
      typeNumber: 0,
      mode: 'Byte',
      errorCorrectionLevel: (design.errorCorrection || 'H') as LibErrorCorrectionLevel
    },
    image: design.logo?.dataUrl || undefined,
    imageOptions,
    dotsOptions,
    cornersSquareOptions,
    cornersDotOptions,
    backgroundOptions
  };
}

/**
 * Helper to instantiate a QRCodeStyling instance with our design config.
 */
export function getQRInstance(
  data: string,
  design: QRDesign,
  size: number = 320
): QRCodeStyling {
  const options = createQRStylingOptions(data, design, size);
  return new QRCodeStyling(options);
}
